import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";

import { ModalService } from "src/app/core/modal.service";
import { NotificationService } from "src/app/core/notification.service";
import { StrikeService } from "src/app/core/strike.service";
import { WalksService } from "src/app/core/walks.service";
import { StrikePicketLines } from "src/app/shared/interfaces/strike-picketlines";
import { StrikeShifts } from "src/app/shared/interfaces/strike-shifts";
import { WalkHistoryDetails } from "src/app/shared/interfaces/walk-history-details";
import { WalkMemberDetails } from "src/app/shared/interfaces/walk-member-details";
import { IsoDatePipe } from "src/app/shared/pipes";

import { ConfirmationModalComponent } from "../../account/modals/confirmation-modal/confirmation-modal.component";
import { EditMemberComponent } from "../modals/edit-member/edit-member.component";
import { EditWalkInfoComponent } from "../modals/edit-walk-info/edit-walk-info.component";

@Component({
  selector: "app-walk-history",
  templateUrl: "./walk-history.component.html",
  styleUrls: ["./walk-history.component.css"],
})
export class WalkHistoryComponent implements OnInit, OnDestroy {
  public subscription: Subscription = new Subscription;
  paramId: string | null = "";
  startDate = new Date();
  endDate = new Date();

  loadingMembers = true;
  strikePicketlines: StrikePicketLines = [];
  strikeShifts: StrikeShifts = [];
  walkHistoryRecords: WalkHistoryDetails[] = [];
  totalCount = 0;
  totalMembers = 0;
  totalHours = 0;
  totalMinutes = 0;
  quickAdjHours = 0;

  page = 0;
  pageSize = 20;
  pagesCount = 0;
  filterMembers = "";
  showDuration = "All";
  newComments = "";

  constructor(
    private strikes: StrikeService,
    private walks: WalksService,
    private route: ActivatedRoute,
    private datepipe: IsoDatePipe,
    private notificationService: NotificationService,
    private modal: ModalService,
  ) { }

  ngOnInit() {
    this.paramId = this.route.snapshot.paramMap.get("id");
    this.search(this.page);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  async search(page?: number) {
    page = page || 0;

    const config = {
      params: {
        page: page,
        pageSize: this.pageSize,
        filter: this.filterMembers,
        showDuration: this.showDuration,
      },
    };

    const formattedStartDate: string = this.datepipe.transform(this.startDate);
    const formattedEndDate: string = this.datepipe.transform(this.endDate);

    const response = await this.strikes.getWalkHistory(
      this.paramId,
      formattedStartDate,
      formattedEndDate,
      config,
    );

    this.walkHistoryRecords = response.Items;
    this.page = response.Page;
    this.pagesCount = response.TotalPages;
    this.totalCount = response.TotalCount;
    this.totalMembers = response.TotalMembers;
    this.totalHours = response.TotalHours;
    this.totalMinutes = response.TotalMinutes;
    this.quickAdjHours = response.QuickAdjustHoursConfigSetting;
    this.loadingMembers = false;

    if (this.filterMembers && this.filterMembers.length) {
      this.notificationService.displayInfo(
        this.totalMembers + " members found",
      );
    }
  }

  clearSearch() {
    this.filterMembers = "";
    this.search();
  }

  openEditMemberModal(memberId: string | undefined) {
    const state = {
      memberId: memberId,
      paramId: this.paramId,
    };

    this.modal.openModal(EditMemberComponent, state);

    this.subscription = this.modal.submitClicked
      .asObservable()
      .subscribe((clicked: boolean) => {
        if (clicked) {
          this.modal.submitClicked.next(false);
        }
        this.subscription.unsubscribe()
      });
  }

  openEditWalkModal(member: WalkMemberDetails) {
    const state = {
      member: member,
      strikeId: this.paramId,
    };

    this.modal.openModal(EditWalkInfoComponent, state);

    this.subscription = this.modal.submitClicked
      .asObservable()
      .subscribe((clicked: boolean) => {
        if (clicked) {
          this.search(this.page).then(() => {
            this.modal.submitClicked.next(false);
          })
        }
        this.subscription.unsubscribe()
      });
  }

  openDeleteModal(member: WalkMemberDetails) {
    const state = {
      title: "Confirm Deletion",
      text: "Are you sure you want to delete this record for " + member.FirstName + " " + member.LastName + "?"
    }

    this.modal.openModal(ConfirmationModalComponent, state);
    this.subscription = this.modal.submitClicked.asObservable().subscribe(async (confirmation) => {
      if(confirmation){
        const response = await this.walks.deleteWalkInfo(member)
        if(response.success) {
          this.search(this.page).then(() => {
            // Set confirmAction in the modalService back to false, if this is not done the modal will immediatly close and return true when next opened
            this.modal.submitClicked.next(false)
          })
        }
        else {
          this.notificationService.displayError('Error deleting the user')
        }
        this.subscription.unsubscribe()
      }
    })
  }

  async adjustHours(member: WalkMemberDetails) {

    member.comments = this.newComments

    if (
      confirm(
        "Are you sure you want to adjust the time to " +
        this.quickAdjHours +
        " hours?",
      )
    ) {
       const response = await this.walks.adjustWalkInfo(member)
       if (response.success) {
          // set properties so that they are updated on the html
          member.Comments = response.body.Comments;
          member.Hours = response.body.Hours;
          member.Minutes = response.body.Minutes;
          member.SignInTime = response.body.SignInTime;
          member.SignOutTime = response.body.SignOutTime;
       }
      else {
        this.notificationService.displayError('Error adjusting hours');
      }
    }
  }

}
